import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import type { ArtistProfileResponse } from 'fym-shared/src/music24/artistProfile';
import { TypedMemo } from '../../../../common/types';
import { useGetArtistProfileQuery } from '../../../../../redux/api/fymAPI';
import { useArtistProfile } from '../../studio/spotifyHeader/useArtistProfile';
import { profileBioMessages as b } from '../bio/profileBioMessages';
import { SpotifyBioSendForm } from './SpotifyBioSendForm';
import { SpotifySheetState } from './SpotifySheetState';
import { spotifyBioSheetView } from './spotifyBioState';
import { useSpotifyBioUpload } from './useSpotifyBioUpload';
import { spotifyBioMessages as s } from './spotifyBioMessages';
import { spotifyUploadMessages as u } from './spotifyUploadMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Profile; each part uses its own slice of it */
import styles from '../Profile.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SpotifyBioSheetProps {
  accountId: number;
  profile: ArtistProfileResponse;
  isOpen: boolean;
  /** Back closes the sheet: the sheet is a step of the history (`&sheet=spotifyBio`). */
  onClose: () => void;
}

/**
 * Upload bio to Spotify: the saved long bio goes to the Spotify page of the artist through the
 * Spotify for Artists sign-in of the Canvas and the images. The view follows the gates of the
 * profile answer (`spotify.bio.unavailable`): the steps with no Auto upload access, Connect or
 * "Sign in again", "Write it first", else the bio to send.
 */
const SpotifyBioSheetComponent: React.FC<SpotifyBioSheetProps> = ({ accountId, profile, isOpen, onClose }) => {
  const intl = useIntl();
  const artistProfile = useArtistProfile(accountId);
  const { refetch } = useGetArtistProfileQuery({ accountId });
  const reload = useCallback(() => {
    refetch().catch(console.error);
  }, [refetch]);
  const artist = profile.spotify.artistName ?? artistProfile.name ?? '';
  const upload = useSpotifyBioUpload(accountId, artist, reload);
  const view = upload.manual ? { kind: 'manual' as const } : spotifyBioSheetView(profile);
  const { reset } = upload;
  const close = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  let body: React.ReactNode;
  if (view.kind === 'send') {
    body = (
      <SpotifyBioSendForm
        profile={profile}
        artist={artist}
        isSending={upload.isSending}
        error={upload.error}
        done={upload.done}
        onSend={upload.send}
        onCancel={close}
      />
    );
  } else if (view.kind === 'manual') {
    body = (
      <div className={styles.howTo} data-testid="spotify-bio-manual">
        <b>{intl.formatMessage(u.manualTitle)}</b>
        <ol>
          <li>{intl.formatMessage(b.whereSpotify1, { artist })}</li>
          <li>{intl.formatMessage(b.whereSpotify2)}</li>
          <li>{intl.formatMessage(b.whereSpotify3)}</li>
        </ol>
      </div>
    );
  } else if (view.kind === 'nothing') {
    body = (
      <span className={styles.meta} data-testid="spotify-bio-nothing">
        {intl.formatMessage(s.nothing)}
      </span>
    );
  } else {
    body = (
      <SpotifySheetState kind={view.kind} accountId={accountId} artist={artist} downloads={[]} onConnected={reload} />
    );
  }

  return (
    <Modal isOpen={isOpen} toggle={close} centered size="lg" data-testid="spotify-bio-sheet">
      <ModalHeader toggle={close}>
        <span className={styles.spotDot} aria-hidden="true" />
        {intl.formatMessage(s.title)}
        {view.kind === 'send' && (
          <small className={`${styles.meta} d-block`}>{intl.formatMessage(u.connected, { artist })}</small>
        )}
      </ModalHeader>
      <ModalBody>{body}</ModalBody>
    </Modal>
  );
};

export const SpotifyBioSheet = TypedMemo(SpotifyBioSheetComponent);
